import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, CheckCircle, AlertTriangle, Wrench, Search, Video, BarChart3, Link2, XCircle } from "lucide-react";
import { getErrorMessage } from "@/lib/errors";
import type { AnyValue } from "@/lib/rowTypes";

interface Lesson {
  lesson_id: string;
  lesson_name: string;
  course_name: string;
  lesson_order: number;
  lesson_video_url: string | null;
  lesson_infographic_url: string | null;
}

interface DiagnosticResult {
  lessonId: string;
  lessonName: string;
  courseName: string;
  mediaType: "video" | "infographic";
  issue: "broken_link" | "orphaned_asset" | "missing_both";
  assetUrl?: string;
  assetId?: string;
  repaired?: boolean;
}

interface MediaDiagnosticsPanelProps {
  lessons: Lesson[];
  onRepairComplete: () => void;
}

export default function MediaDiagnosticsPanel({ lessons, onRepairComplete }: MediaDiagnosticsPanelProps) {
  const [scanning, setScanning] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [results, setResults] = useState<DiagnosticResult[] | null>(null);
  const [repairLog, setRepairLog] = useState<string[]>([]);

  const runScan = useCallback(async () => {
    setScanning(true);
    setResults(null);
    setRepairLog([]);

    try {
      // Load all asset records
      const [{ data: infographicAssets }, { data: videoAssets }] = await Promise.all([
        supabase.from("infographic_assets").select("id, file_name, file_url, assigned_lesson_id, status"),
        supabase.from("video_assets").select("id, file_name, file_url, assigned_lesson_id, status"),
      ]);

      const diagnostics: DiagnosticResult[] = [];

      // Build lookup of asset URLs by lesson ID
      const infographicByLesson = new Map<string, { id: string; url: string }>();
      const videoByLesson = new Map<string, { id: string; url: string }>();

      for (const a of (infographicAssets || [])) {
        if (a.assigned_lesson_id && a.status === "assigned") {
          infographicByLesson.set(a.assigned_lesson_id, { id: a.id, url: a.file_url });
        }
      }
      for (const a of (videoAssets || [])) {
        if (a.assigned_lesson_id && a.status === "assigned") {
          videoByLesson.set(a.assigned_lesson_id, { id: a.id, url: a.file_url });
        }
      }

      for (const lesson of lessons) {
        // Check infographic: lesson says missing but asset exists assigned to it
        if (!lesson.lesson_infographic_url && infographicByLesson.has(lesson.lesson_id)) {
          const asset = infographicByLesson.get(lesson.lesson_id)!;
          diagnostics.push({
            lessonId: lesson.lesson_id,
            lessonName: lesson.lesson_name,
            courseName: lesson.course_name,
            mediaType: "infographic",
            issue: "broken_link",
            assetUrl: asset.url,
            assetId: asset.id,
          });
        }

        // Check video: lesson says missing but asset exists assigned to it
        if (!lesson.lesson_video_url && videoByLesson.has(lesson.lesson_id)) {
          const asset = videoByLesson.get(lesson.lesson_id)!;
          diagnostics.push({
            lessonId: lesson.lesson_id,
            lessonName: lesson.lesson_name,
            courseName: lesson.course_name,
            mediaType: "video",
            issue: "broken_link",
            assetUrl: asset.url,
            assetId: asset.id,
          });
        }

        // Check reverse: lesson has URL but no asset row points to it
        if (lesson.lesson_infographic_url && !infographicByLesson.has(lesson.lesson_id)) {
          // Check if ANY infographic asset has this URL
          const matchingAsset = (infographicAssets || []).find((a: AnyValue) => a.file_url === lesson.lesson_infographic_url);
          if (!matchingAsset) {
            diagnostics.push({
              lessonId: lesson.lesson_id,
              lessonName: lesson.lesson_name,
              courseName: lesson.course_name,
              mediaType: "infographic",
              issue: "orphaned_asset",
              assetUrl: lesson.lesson_infographic_url,
            });
          }
        }

        if (lesson.lesson_video_url && !videoByLesson.has(lesson.lesson_id)) {
          const matchingAsset = (videoAssets || []).find((a: AnyValue) => a.file_url === lesson.lesson_video_url);
          if (!matchingAsset) {
            diagnostics.push({
              lessonId: lesson.lesson_id,
              lessonName: lesson.lesson_name,
              courseName: lesson.course_name,
              mediaType: "video",
              issue: "orphaned_asset",
              assetUrl: lesson.lesson_video_url,
            });
          }
        }
      }

      setResults(diagnostics);
      if (diagnostics.length === 0) {
        toast.success("All media references are consistent — no issues found");
      } else {
        toast.warning(`Found ${diagnostics.length} media issue(s)`);
      }
    } catch (err: unknown) {
      toast.error(`Scan failed: ${getErrorMessage(err)}`);
    } finally {
      setScanning(false);
    }
  }, [lessons]);

  const runRepair = useCallback(async () => {
    if (!results) return;
    const repairable = results.filter((r) => r.issue === "broken_link" && r.assetUrl);
    if (repairable.length === 0) {
      toast.info("No automatically repairable issues found");
      return;
    }

    setRepairing(true);
    const log: string[] = [];

    for (const item of repairable) {
      try {
        const field = item.mediaType === "video" ? "lesson_video_url" : "lesson_infographic_url";
        await supabase
          .from("lessons")
          .update({ [field]: item.assetUrl } as AnyValue)
          .eq("lesson_id", item.lessonId);
        
        item.repaired = true;
        log.push(`✓ Repaired ${item.mediaType} for "${item.lessonName}" (${item.courseName})`);
      } catch (err: unknown) {
        log.push(`✗ Failed: ${item.lessonName} — ${getErrorMessage(err)}`);
      }
    }

    setRepairLog(log);
    setRepairing(false);
    toast.success(`Repaired ${repairable.filter((r) => r.repaired).length} broken link(s)`);
    onRepairComplete();
  }, [results, onRepairComplete]);

  const brokenLinks = results?.filter((r) => r.issue === "broken_link") || [];
  const orphaned = results?.filter((r) => r.issue === "orphaned_asset") || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-semibold text-sm text-foreground">Media Diagnostics</h2>
          <p className="text-xs text-muted-foreground font-body mt-1">
            Scan for broken links between lessons and media assets, then auto-repair
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={runScan}
            disabled={scanning}
            className="gap-1.5"
          >
            {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            {scanning ? "Scanning..." : "Run Scan"}
          </Button>
          {brokenLinks.length > 0 && (
            <Button
              size="sm"
              onClick={runRepair}
              disabled={repairing}
              className="gap-1.5 bg-accent/15 border border-accent/30 text-accent hover:bg-accent/25"
            >
              {repairing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5" />}
              {repairing ? "Repairing..." : `Repair ${brokenLinks.filter((r) => !r.repaired).length} Broken Link(s)`}
            </Button>
          )}
        </div>
      </div>

      {results === null && !scanning && (
        <div className="rounded-xl border border-border bg-card p-8 text-center">
          <Search className="w-8 h-8 mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-sm font-body text-muted-foreground">Click "Run Scan" to check media consistency</p>
        </div>
      )}

      {results && results.length === 0 && (
        <div className="rounded-xl border border-accent/30 bg-accent/10 p-4 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-accent shrink-0" />
          <p className="text-xs font-body text-accent">All media references are consistent. No broken links detected.</p>
        </div>
      )}

      {brokenLinks.length > 0 && (
        <div className="rounded-xl border border-secondary/30 bg-secondary/10 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-secondary shrink-0" />
            <span className="text-xs font-heading font-semibold text-secondary">
              {brokenLinks.length} Broken Link(s) — asset assigned but lesson URL missing
            </span>
          </div>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {brokenLinks.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-xs font-body px-2 py-1.5 rounded-lg bg-muted/30">
                {item.repaired ? (
                  <CheckCircle className="w-3.5 h-3.5 text-accent shrink-0" />
                ) : (
                  <XCircle className="w-3.5 h-3.5 text-secondary shrink-0" />
                )}
                {item.mediaType === "video" ? <Video className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <BarChart3 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                <span className="truncate text-muted-foreground">
                  {item.courseName} → {item.lessonName}
                  {item.repaired && <span className="text-accent ml-1">— repaired</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {orphaned.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="text-xs font-heading font-semibold text-foreground">
              {orphaned.length} Orphaned Reference(s) — lesson has URL but no asset row
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground font-body">
            These lessons reference a media URL that has no matching asset record. The media may still display correctly if the file exists in storage.
          </p>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {orphaned.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-xs font-body px-2 py-1.5 rounded-lg bg-muted/30">
                {item.mediaType === "video" ? <Video className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <BarChart3 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                <span className="truncate text-muted-foreground">{item.courseName} → {item.lessonName}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {repairLog.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-2">
          <h3 className="text-xs font-heading font-semibold text-foreground">Repair Log</h3>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {repairLog.map((line, i) => (
              <p key={i} className="text-[10px] font-body text-muted-foreground">{line}</p>
            ))}
          </div>
        </div>
      )}

      {/* Lesson Media Summary Table */}
      {results !== null && (
        <LessonMediaSummary lessons={lessons} />
      )}
    </div>
  );
}

/** Quick admin visibility: which lessons have video/infographic */
function LessonMediaSummary({ lessons }: { lessons: Lesson[] }) {
  const [courseFilter, setCourseFilter] = useState<string>("all");
  const courseNames = [...new Set(lessons.map((l) => l.course_name))].sort();
  const filtered = courseFilter === "all" ? lessons : lessons.filter((l) => l.course_name === courseFilter);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-heading font-semibold text-foreground">Lesson Media Reference Map</h3>
        <select
          value={courseFilter}
          onChange={(e) => setCourseFilter(e.target.value)}
          className="text-xs bg-card border border-border rounded-md px-2 py-1 text-foreground"
        >
          <option value="all">All Courses</option>
          {courseNames.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
      </div>
      <div className="rounded-xl border border-border bg-card overflow-hidden max-h-96 overflow-y-auto">
        <table className="w-full text-xs font-body">
          <thead className="bg-muted/30 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 text-muted-foreground font-medium">Lesson</th>
              <th className="text-center px-2 py-2 text-muted-foreground font-medium w-16">
                <Video className="w-3 h-3 mx-auto" />
              </th>
              <th className="text-center px-2 py-2 text-muted-foreground font-medium w-16">
                <BarChart3 className="w-3 h-3 mx-auto" />
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((lesson) => (
              <tr key={lesson.lesson_id} className="border-t border-border/50">
                <td className="px-3 py-1.5 truncate max-w-[200px]" title={`${lesson.course_name} → ${lesson.lesson_name}`}>
                  <span className="text-muted-foreground">{lesson.course_name} → </span>
                  {lesson.lesson_name}
                </td>
                <td className="text-center px-2 py-1.5">
                  {lesson.lesson_video_url ? (
                    <CheckCircle className="w-3.5 h-3.5 text-accent mx-auto" />
                  ) : (
                    <XCircle className="w-3.5 h-3.5 text-muted-foreground/30 mx-auto" />
                  )}
                </td>
                <td className="text-center px-2 py-1.5">
                  {lesson.lesson_infographic_url ? (
                    <CheckCircle className="w-3.5 h-3.5 text-accent mx-auto" />
                  ) : (
                    <XCircle className="w-3.5 h-3.5 text-muted-foreground/30 mx-auto" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
